import { matchPath } from "react-router-dom"

// Route constants in this codebase are mixed: some include a leading slash,
// others do not. Normalize once so matching is consistent everywhere.
export function normalizeRoutePath(path) {
  if (typeof path !== "string" || path.length === 0) {
    return ""
  }

  return path.startsWith("/") ? path : `/${path}`
}

// Determine if a given route path is absolute (starts with a leading slash).
function isAbsoluteRoutePath(path) {
  return typeof path === "string" && path.startsWith("/")
}

// Join a base route path with a nested route path, ensuring proper slashes.
function joinRoutePaths(basePath, nestedPath) {
  const normalizedBase = normalizeRoutePath(basePath).replace(/\/+$/, "")
  const normalizedNested = `${nestedPath || ""}`.replace(/^\/+/, "")

  if (!normalizedBase || !normalizedNested) {
    return normalizeRoutePath(basePath)
  }

  return `${normalizedBase}/${normalizedNested}`
}

function getRouteCandidatePaths(route, landingRoutes) {
  const routePath = route?.path

  if (typeof routePath !== "string" || routePath.length === 0) {
    return []
  }

  const directPath = normalizeRoutePath(routePath)
  // First candidate is the literal route definition after normalization.
  const candidates = [{ path: directPath, inheritedFromLandingRoute: null }]

  // Why we generate extra candidates for relative paths:
  // - Some route constants are intentionally relative (for example GPRA child paths
  //   like "recordMgmt" or "intake/demographics").
  // - On navigation, the real URL is nested under a landing path
  //   (for example "/participant/gpra-forms/recordMgmt").
  // - If we only normalize the relative child to "/recordMgmt", exact matching
  //   fails and no route metadata is available to layout/sidebar logic.
  //
  // To preserve existing behavior, we also test each relative path under every
  // landing route and keep the landing route reference. If that nested candidate
  // wins, callers can inherit landing metadata (e.g. sidebarContentGroup) when
  // the child route does not define it directly.
  // For relative route constants, also test "landingPath/childPath" candidates
  // so URLs like /participant/gpra-forms/recordMgmt can match metadata routes
  // defined as just "recordMgmt".
  if (!isAbsoluteRoutePath(routePath)) {
    landingRoutes.forEach((landingRoute) => {
      // Mirror React Router nested-path expansion.
      const nestedResolvedPath = joinRoutePaths(landingRoute.path, routePath)

      // Avoid adding duplicate candidates.
      if (nestedResolvedPath !== directPath) {
        candidates.push({
          // Keep the landing route so caller can inherit missing metadata.
          path: nestedResolvedPath,
          inheritedFromLandingRoute: landingRoute,
        })
      }
    })
  }

  return candidates
}

// Compute a simple specificity score so we can prefer the most explicit route
// when more than one route pattern matches the same pathname.
function getRouteSpecificityScore(path) {
  const normalizedPath = normalizeRoutePath(path)
  const segments = normalizedPath.split("/").filter(Boolean)
  const dynamicSegmentCount = segments.filter((segment) =>
    segment.startsWith(":"),
  ).length

  return {
    dynamicSegmentCount,
    segmentCount: segments.length,
    charLength: normalizedPath.length,
  }
}

// Merge route metadata from a landing route into a child route, giving precedence
// to the child route's own values. This allows nested routes to inherit context
// like sidebarContentGroup or profileInfoMode from their parent landing route.
function mergeInheritedRouteMetadata(route, inheritedFromLandingRoute) {
  if (!route || !inheritedFromLandingRoute) {
    return route
  }

  // Merge landing metadata first so child route values remain authoritative.
  return {
    ...inheritedFromLandingRoute,
    ...route,
  }
}

// Match all candidate routes, then pick the most specific one.
// This mirrors React Router's ranking intent and avoids cases where a broad
// dynamic route (e.g. /organization/staff/:idFilter) shadows a static route
// (e.g. /organization/staff/profile).
export function getMostSpecificMatchedRoute(routes, pathname) {
  const landingRoutes = (routes || []).filter(
    (route) => route?.landing && typeof route?.path === "string",
  )

  // Match against direct and nested candidates, and keep the specific matched
  // path plus landing context for later tie-breaking and metadata fallback.
  const matchingRoutes = (routes || []).flatMap((route) => {
    const candidates = getRouteCandidatePaths(route, landingRoutes)
    const matchedCandidate = candidates.find((candidate) =>
      Boolean(matchPath({ path: candidate.path, end: true }, pathname)),
    )

    if (!matchedCandidate) {
      return []
    }

    return [
      {
        route,
        matchedPath: matchedCandidate.path,
        inheritedFromLandingRoute: matchedCandidate.inheritedFromLandingRoute,
      },
    ]
  })

  if (matchingRoutes.length <= 1) {
    // If only one route matches this URL, return it directly (after optional
    // landing metadata inheritance) instead of running tie-break logic.
    const onlyMatch = matchingRoutes[0]

    if (!onlyMatch) {
      return onlyMatch
    }

    // Inherit landing metadata (e.g. profileInfoMode/sidebarContentGroup) for
    // nested child matches; child-defined values still override inherited ones.
    return mergeInheritedRouteMetadata(
      onlyMatch.route,
      onlyMatch.inheritedFromLandingRoute,
    )
  }

  // Ranking:
  // 1) fewer dynamic segments ("/:id")
  // 2) more path segments (deeper route)
  // 3) longer path as a final tie-breaker
  const bestMatch = matchingRoutes.reduce((bestRoute, currentRoute) => {
    if (!bestRoute) {
      return currentRoute
    }

    const bestScore = getRouteSpecificityScore(bestRoute.matchedPath)
    const currentScore = getRouteSpecificityScore(currentRoute.matchedPath)

    if (currentScore.dynamicSegmentCount !== bestScore.dynamicSegmentCount) {
      return currentScore.dynamicSegmentCount < bestScore.dynamicSegmentCount
        ? currentRoute
        : bestRoute
    }

    if (currentScore.segmentCount !== bestScore.segmentCount) {
      return currentScore.segmentCount > bestScore.segmentCount
        ? currentRoute
        : bestRoute
    }

    if (currentScore.charLength !== bestScore.charLength) {
      return currentScore.charLength > bestScore.charLength
        ? currentRoute
        : bestRoute
    }

    return bestRoute
  }, null)

  if (!bestMatch) {
    return null
  }

  return mergeInheritedRouteMetadata(
    bestMatch.route,
    bestMatch.inheritedFromLandingRoute,
  )
}